import React, { useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Sparkles, CheckCircle2 } from "lucide-react";

export default function NewsletterSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    consent_email: false,
    consent_sms: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    if (!form.consent_email) {
      toast.error("Please confirm the email opt-in to continue.");
      return;
    }
    if (!form.consent_sms) {
      toast.error("Please confirm the SMS opt-in to continue.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", form);
      setDone(true);
      toast.success("You're on the list. Watch your inbox and messages.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative px-4 sm:px-8 py-24 bg-[#080808] border-y border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/10 blur-[160px]" />
        <div className="absolute -bottom-40 right-1/4 w-[400px] h-[400px] rounded-full bg-[#E52E71]/10 blur-[140px]" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-4">
            <Sparkles size={14} /> Join the inner circle
          </div>
          <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
            Weekly protocols,<br />
            <span className="text-brand-gradient">delivered to you.</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-md leading-relaxed">
            Get Dr. Slava&apos;s latest peptide, hormone and mindset protocols — plus early
            access to workshops and cohorts. No fluff, no spam.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/50">
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#FF8A00]" /> Free forever</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#FF8A00]" /> Unsubscribe anytime</span>
          </div>
        </div>

        <div className="glass rounded-2xl p-6 sm:p-8 border border-white/10">
          {done ? (
            <div className="text-center py-8" data-testid="newsletter-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center">
                <Mail size={22} />
              </div>
              <div className="font-display text-2xl mt-5">You&apos;re in.</div>
              <p className="text-sm text-white/60 mt-2">
                Check your inbox — your first protocol arrives shortly. We&apos;ll text
                you a confirmation shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="newsletter-form">
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Name</label>
                <Input
                  data-testid="newsletter-name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Email</label>
                <Input
                  data-testid="newsletter-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@domain.com"
                  className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Mobile phone</label>
                <Input
                  data-testid="newsletter-phone"
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 123 4567"
                  className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                />
              </div>

              {/* Consent block */}
              <div className="pt-2 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-email-row">
                  <Checkbox
                    checked={form.consent_email}
                    onCheckedChange={(v) => setForm({ ...form, consent_email: !!v })}
                    data-testid="newsletter-consent-email"
                    className="mt-0.5 border-white/25 data-[state=checked]:bg-[#FF8A00] data-[state=checked]:border-[#FF8A00]"
                  />
                  <span className="text-[12px] leading-[1.55] text-white/60 group-hover:text-white/75 transition">
                    <span className="text-white/85 font-medium">Email opt-in.</span>{" "}
                    I agree to receive marketing and educational emails from Dr Thrive 365
                    at the address I provided. I understand I can unsubscribe at any time
                    using the link in any email.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-sms-row">
                  <Checkbox
                    checked={form.consent_sms}
                    onCheckedChange={(v) => setForm({ ...form, consent_sms: !!v })}
                    data-testid="newsletter-consent-sms"
                    className="mt-0.5 border-white/25 data-[state=checked]:bg-[#FF8A00] data-[state=checked]:border-[#FF8A00]"
                  />
                  <span className="text-[12px] leading-[1.55] text-white/60 group-hover:text-white/75 transition">
                    <span className="text-white/85 font-medium">SMS &amp; WhatsApp opt-in.</span>{" "}
                    I agree to receive recurring marketing and protocol text messages
                    (including via WhatsApp Business) from Dr Thrive 365 at the mobile
                    number I provided. Message frequency varies. Message &amp; data rates
                    may apply. Consent is not a condition of any purchase. Reply{" "}
                    <span className="font-mono text-white/80">STOP</span> to cancel or{" "}
                    <span className="font-mono text-white/80">HELP</span> for help.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="w-full rounded-full h-12 mt-3 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 text-white border-0 disabled:opacity-60"
              >
                {loading ? "Subscribing..." : "Send me protocols"}
              </Button>

              <p className="text-[11px] text-white/35 text-center leading-relaxed">
                By subscribing you agree to our{" "}
                <a href="/privacy" className="underline hover:text-white/60">Privacy Policy</a>{" "}
                and{" "}
                <a href="/terms" className="underline hover:text-white/60">Terms</a>.
                This content is educational only, not medical advice.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
